/** Cores do peão Esmeralda (EveryPower) — wiki xat + gs.xatblog.net. */
export type EverypowerPawnColor = {
  label: string;
  hat: string | null;
  hex: string;
  /** Nome interno do peão para pré-visualização (gs.xatblog.net). */
  pawnName: string;
};

export type EverypowerPawn = {
  hat: string;
  name: string;
};

/** Pré-visualizações locais em `/pawns` (smilies everypawn*). */
export const EVERYPOWER_PAWN_LOCAL_IMAGES: Record<string, string> = {
  everypawnpink: '/pawns/everypawnpink.webp',
  everypawnblueman: '/pawns/everypawnblueman.webp',
  everypawnpurple: '/pawns/everypawnpurple.webp',
  everypawngold: '/pawns/everypawngold.webp',
  everypawnruby: '/pawns/everypawnruby.webp',
  everypawnsupporter: '/pawns/everypawnsupport.webp',
  everypawnyellow: '/pawns/everypawnyellow.webp',
};

export function getEverypowerPawnLocalImage(code: string): string | null {
  const key = code.trim().toLowerCase();
  return EVERYPOWER_PAWN_LOCAL_IMAGES[key] ?? null;
}

/** Peões Esmeralda — hat codes + smilies de pré-visualização (`/pawns` + gs.xatblog.net). */
export const EVERYPOWER_PAWNS: EverypowerPawn[] = [
  { hat: '(hat#Ehh)', name: 'p1emerald' },
  { hat: '(hat#Ekh)', name: 'everypawnpink' },
  { hat: '(hat#Ebh)', name: 'everypawnblueman' },
  { hat: '(hat#Eph)', name: 'everypawnpurple' },
  { hat: '(hat#Egh)', name: 'everypawngold' },
  { hat: '(hat#Erh)', name: 'everypawnruby' },
  { hat: '(hat#Evh)', name: 'everypawnsupporter' },
  { hat: '(hat#Eyh)', name: 'everypawnyellow' },
];

export const EVERYPOWER_PAWN_COLORS: EverypowerPawnColor[] = [
  { label: 'Verde (padrão)', hat: '(hat#Ehh)', hex: '#50c878', pawnName: 'p1emerald' },
  { label: 'Rosa', hat: '(hat#Ekh)', hex: '#ff69b4', pawnName: 'everypawnpink' },
  { label: 'Azul', hat: '(hat#Ebh)', hex: '#000080', pawnName: 'everypawnblueman' },
  { label: 'Roxo', hat: '(hat#Eph)', hex: '#800080', pawnName: 'everypawnpurple' },
  { label: 'Dourado', hat: '(hat#Egh)', hex: '#f4c75f', pawnName: 'everypawngold' },
  { label: 'Rubi', hat: '(hat#Erh)', hex: '#dc143c', pawnName: 'everypawnruby' },
  { label: 'Céu', hat: '(hat#Evh)', hex: '#5ab9ed', pawnName: 'everypawnsupporter' },
  { label: 'Amarelo', hat: '(hat#Eyh)', hex: '#ffff00', pawnName: 'everypawnyellow' },
];

/** Dica por código hat — cor Everypower, power associado e imagens (`/pawns`). */
export type EverypowerPawnTooltipData = {
  text: string;
  powerLabel: string;
  everypawnLightSrc: string | null;
  everypawnDarkSrc: string | null;
  referencePawnLightSrc: string | null;
  referencePawnDarkSrc: string | null;
  /** Peões normais (pink, blueman, etc.) são mais pequenos no tooltip. */
  referencePawnCompact?: boolean;
};

const pawnPublic = (file: string) => `/pawns/${file}`;

export const EVERYPOWER_PAWN_TOOLTIP_DATA: Record<string, EverypowerPawnTooltipData> = {
  '(hat#ehh)': {
    text: 'Peão Esmeralda verde (padrão).',
    powerLabel: 'EveryPower',
    everypawnLightSrc: null,
    everypawnDarkSrc: null,
    referencePawnLightSrc: null,
    referencePawnDarkSrc: null,
  },
  '(hat#ekh)': {
    text: 'Cor rosa — combina com o power Pink.',
    powerLabel: 'Pink',
    everypawnLightSrc: pawnPublic('everypawnpink.webp'),
    everypawnDarkSrc: pawnPublic('everypawnpink.webp'),
    referencePawnLightSrc: pawnPublic('pink.webp'),
    referencePawnDarkSrc: pawnPublic('pink.webp'),
    referencePawnCompact: true,
  },
  '(hat#ebh)': {
    text: 'Cor azul — combina com o power Blueman.',
    powerLabel: 'Blueman',
    everypawnLightSrc: pawnPublic('everypawnblueman.webp'),
    everypawnDarkSrc: pawnPublic('everypawnblueman.webp'),
    referencePawnLightSrc: pawnPublic('blueman.webp'),
    referencePawnDarkSrc: pawnPublic('blueman.webp'),
    referencePawnCompact: true,
  },
  '(hat#eph)': {
    text: 'Cor roxa — combina com o power Purple.',
    powerLabel: 'Purple',
    everypawnLightSrc: pawnPublic('everypawnpurple.webp'),
    everypawnDarkSrc: pawnPublic('everypawnpurple.webp'),
    referencePawnLightSrc: pawnPublic('purple.webp'),
    referencePawnDarkSrc: pawnPublic('purple.webp'),
    referencePawnCompact: true,
  },
  '(hat#egh)': {
    text: 'Cor dourada — combina com o power Gold.',
    powerLabel: 'Gold',
    everypawnLightSrc: pawnPublic('everypawngold.webp'),
    everypawnDarkSrc: pawnPublic('everypawngold.webp'),
    referencePawnLightSrc: pawnPublic('gold.webp'),
    referencePawnDarkSrc: pawnPublic('gold.webp'),
  },
  '(hat#erh)': {
    text: 'Cor rubi — combina com o power Ruby.',
    powerLabel: 'Ruby',
    everypawnLightSrc: pawnPublic('everypawnruby.webp'),
    everypawnDarkSrc: pawnPublic('everypawnruby.webp'),
    referencePawnLightSrc: pawnPublic('ruby.webp'),
    referencePawnDarkSrc: pawnPublic('ruby.webp'),
  },
  '(hat#evh)': {
    text: 'Cor céu (Supporter) — requer o peão sky de Supporter activo.',
    powerLabel: 'Supporter',
    everypawnLightSrc: pawnPublic('everypawnsupport.webp'),
    everypawnDarkSrc: pawnPublic('everypawnsupport.webp'),
    referencePawnLightSrc: pawnPublic('sky.png'),
    referencePawnDarkSrc: pawnPublic('sky.png'),
    referencePawnCompact: true,
  },
  '(hat#eyh)': {
    text: 'Cor amarela — requer o power Yellow (ace).',
    powerLabel: 'Yellow',
    everypawnLightSrc: pawnPublic('everypawnyellow.webp'),
    everypawnDarkSrc: pawnPublic('everypawnyellow.webp'),
    referencePawnLightSrc: pawnPublic('yellow.png'),
    referencePawnDarkSrc: pawnPublic('yellow.png'),
    referencePawnCompact: true,
  },
};

/** @deprecated Use `getEverypowerPawnTooltipData`. */
export const EVERYPOWER_PAWN_TOOLTIPS: Record<string, string> = Object.fromEntries(
  Object.entries(EVERYPOWER_PAWN_TOOLTIP_DATA).map(([k, v]) => [k, v.text])
);

export function getEverypowerPawnTooltipData(hat: string): EverypowerPawnTooltipData | null {
  const key = hat.trim().toLowerCase();
  return EVERYPOWER_PAWN_TOOLTIP_DATA[key] ?? null;
}

export function getEverypowerPawnTooltip(hat: string): string | null {
  return getEverypowerPawnTooltipData(hat)?.text ?? null;
}

export const EVERYPOWER_DESCRIPTION = {
  short: 'Peão Esmeralda ao possuir todos os powers do xat.',
  long: 'EveryPower é uma funcionalidade especial que desbloqueia o peão Esmeralda quando você possui pelo menos um exemplar de cada power já lançado no xat. Novos powers são lançados semanalmente — é preciso adquiri-los para manter o EveryPower activo. Também coloca você mais acima na lista de visitantes do que qualquer outro utilizador do mesmo cargo (complementa o efeito do Topman). Para desactivar, digite /-everypower no chat ou desligue na lista de Powers.',
} as const;
